/**
 * Hand-drawn tower art, keyed by upgrade state.
 *
 * A tower used to be one plate and one barrel, and a 2-1-0 Turret with $385
 * sunk into it drew exactly the same twelve pixels as the $200 one beside it.
 * BTD6's whole upgrade loop rests on the opposite: the model changes, so the
 * board tells you what you bought without a panel open.
 *
 * ## Three layers, three axes
 *
 * Each tower draws as up to three sprites stacked on one tile:
 *
 *   plate    the ground footprint  — static, drawn first, widest
 *   barrel   the thing that points — rotates to the aim angle
 *   crown    the detail on top     — optics, tanks, prongs, discs
 *
 * Each layer is driven by exactly one upgrade path, and **which** path drives
 * which layer is decided per tower by what that path does in the catalogue. A
 * Turret's rate path adds barrels, so it drives the barrel layer; a Laser's
 * split path adds *emitters*, so on that tower the barrel layer belongs to path
 * 1 and the ramp path gets the radiator crown instead. The rule held across all
 * ten is that no two paths share a layer, which is what makes 5-0-0 and 0-0-5
 * different shapes rather than different shades.
 *
 * ## Why five states and not six
 *
 * `state = BRACKET[tier]` maps six tiers onto five drawings: 0, 1, 2, 3-4, 5.
 * Only tiers 3 and 4 share, and they share the largest silhouette, where one
 * tier's worth of growth would have been lost among thirty-two pixels anyway.
 * Every purchase below that changes the drawing, which is what stops a 2-1-0
 * from looking like a 1-2-0.
 *
 * The tiers inside a bracket are not silent. Every sprite carries `+` pixels —
 * the hot detail channel — and the renderer drives their intensity, their pulse
 * rate and their bleed toward white from the *exact* tier rather than the
 * bracket. Tier 2 is a hotter tier 1; tier 4 throbs where tier 3 glows steady.
 * Silhouette moves on 1, 3 and 5. Light moves on all five.
 *
 * ## Sizes
 *
 * States 0, 1 and 2 draw at 16, state 3 at 24, state 4 at 32. The crossover rule
 * in `game/towers/Upgrades.ts` allows at most one path above tier 2, so at most
 * one layer is ever larger than 16 and the big states never fight each other.
 *
 * ## Encoding
 *
 * One character per pixel, `rows[0]` is the top row:
 *
 *   `.`        empty
 *   `1`..`5`   shade ramp, dark to over-bright, indexing {@link SHADES}
 *   `+`        hot detail — the emissive channel the tier ramp drives
 *
 * Rows may be **shorter** than `size` and are right-padded with empty. Trailing
 * dots carry no information and counting them by hand is how a sprite ends up
 * sheared by a pixel. Row *count* must be exact: vertical placement does carry
 * information, and a line is easy to count.
 *
 * Nothing here imports Three.js. It is plain data, safe in a test or a worker.
 */

// ────────────────────────────────────────────────────────────────── types ──

export interface TowerSpriteDef {
  /** Edge of the art box in pixels. Always square. */
  readonly size: number;
  /** Exactly `size` rows of at most `size` characters, top row first. */
  readonly rows: readonly string[];
  /**
   * X of the muzzle in art coordinates. Barrels only: the renderer puts the
   * flash here, so a longer barrel's flash travels out with the tip instead of
   * blooming from the middle of the receiver.
   */
  readonly muzzle?: number;
  /**
   * Turns per second. Non-zero replaces the layer's angle with a clock, which
   * is how a tier-5 radar sweeps and a gravity ring spins without the sim
   * knowing anything about it.
   */
  readonly spin?: number;
}

/**
 * Shade ramp, indexed by the characters `1`..`5`. Index 0 is empty and exists
 * only so a character's numeric value indexes this directly.
 *
 * Stored halved in the atlas so an 8-bit channel holds the over-bright top end;
 * the shader doubles it back.
 */
export const SHADES = [0, 0.18, 0.34, 0.52, 0.76, 1.12] as const;

/**
 * Base shade of a hot pixel, before the per-tier drive.
 *
 * Deliberately near the top of the ordinary ramp rather than above it. A hot
 * pixel is lit twice — once as shade, once as the additive tier drive — and
 * setting this over 1.0 made a tier-5 emitter clip to a white ball with no
 * structure left in it. The punch belongs to the drive.
 */
export const HOT_SHADE = 0.85;

// ──────────────────────────────────────────────────────── shared plates (0) ──
//
// State 0 of the plate layer is the tower's weight class, not its identity: an
// un-upgraded mount is a mount. Four designs cover ten towers, which is what
// `TowerBehaviour.plate` in the catalogue has always meant.

/** Light: a small octagonal pad. Flak, Acid, Sniper. */
const plateLight: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '',
    '.....222222',
    '....23333332',
    '...2334444332',
    '...2344++4432',
    '...2344++4432',
    '...2334444332',
    '....23333332',
    '.....222222',
    '', '', '', '',
  ],
};

/** Medium: a bolted octagon with a raised hub. Turret, Tesla, Cryo, Laser. */
const plateMedium: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '....22222222',
    '..223333333322',
    '..233444444332',
    '.23344444444332',
    '.23444++++44432',
    '.23444++++44432',
    '.23344444444332',
    '..233444444332',
    '..223333333322',
    '....22222222',
    '', '', '',
  ],
};

/** Heavy: a full pad with cut corners and cross bracing. Mortar. */
const plateHeavy: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '...2222222222',
    '..233333333332',
    '.23334444433332',
    '.23444444444432',
    '2334444444444332',
    '2344444++4444432',
    '2344444++4444432',
    '2334444444444332',
    '.23444444444432',
    '.23334444433332',
    '..233333333332',
    '...2222222222',
    '', '',
  ],
};

/** Support: a ring pad with an open centre. Pylon, Singularity. */
const plateSupport: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '....22222222',
    '...2344444432',
    '..23444444432',
    '..2344....4432',
    '..234+....+432',
    '..234+....+432',
    '..2344....4432',
    '..23444444432',
    '...2344444432',
    '....22222222',
    '', '', '',
  ],
};

// ───────────────────────────────────────────────────────────────── turret ──
//
// path 0 rate       → barrel   one gun, two guns, a spool, a drum
// path 1 precision  → crown    glass on the receiver, growing into an optics
//                              package with rangefinder ears
// path 2 mass       → plate    bolts, then rails and capacitors, then a
//                              coil-pylon foundation
//
// The crown follows the aim angle. A sight that stays north while the gun
// swings east reads as a bug, not as a sight.

const turretBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '', '', '',
    '...2222',
    '..233332',
    '..2344432222',
    '..234444333335',
    '..234444333335',
    '..2344432222',
    '..233332',
    '...2222',
    '', '', '', '',
  ],
};

const turretBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '', '',
    '..222',
    '..2+2',
    '..2+22222',
    '..2333332',
    '..23444432222',
    '..234444433335',
    '..234444433335',
    '..23444432222',
    '..2333332',
    '...22222',
    '', '', '',
  ],
};

const turretBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 21,
  rows: [
    '', '', '', '', '', '',
    '....22222',
    '...2333332',
    '...23444432222222',
    '...234+44433333333335',
    '...234+44433333333335',
    '...2344443',
    '...2344443',
    '...234+44433333333335',
    '...234+44433333333335',
    '...23444432222222',
    '...2333332',
    '....22222',
    '', '', '', '', '', '',
  ],
};

const turretBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 29,
  rows: [
    '', '', '', '', '', '', '',
    '.....2222222',
    '....233333332',
    '...23344444332222',
    '...234+4444433333333333335',
    '...234+4444433333333333335',
    '...23344444332222',
    '..2334444444432',
    '..234+444444443222222222',
    '..234+44444444433333333333335',
    '..234+44444444433333333333335',
    '..234+444444443222222222',
    '..2334444444432',
    '...23344444332222',
    '...234+4444433333333333335',
    '...234+4444433333333333335',
    '...23344444332222',
    '....233333332',
    '.....2222222',
    '', '', '', '', '', '', '',
  ],
};

const turretCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '', '',
    '.....22222',
    '....2344432',
    '....234+++2',
    '....2344432',
    '.....22222',
    '', '', '', '', '', '',
  ],
};

const turretCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '', '', '', '', '', '', '',
    '........2222222',
    '.......233333332',
    '.......23444443222',
    '.......234+++4433352',
    '.......234+++4433352',
    '.......23444443222',
    '.......233333332',
    '........2222222',
    '', '', '', '', '', '', '', '',
  ],
};

const turretCrown4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '', '', '', '', '', '',
    '...........2222',
    '..........23++32',
    '..........2333332',
    '........222222222',
    '.......23333333332',
    '......2344444444432',
    '......234+++++++432222',
    '......234+5...5+43333352',
    '......234+5...5+43333352',
    '......234+++++++432222',
    '......2344444444432',
    '.......23333333332',
    '........222222222',
    '..........2333332',
    '..........23++32',
    '...........2222',
    '', '', '', '', '', '', '', '',
  ],
};

const turretPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '...2222222222',
    '..233333333332',
    '..234444444432',
    '..2+4444444+32',
    '..234444444432',
    '..234444444432',
    '..2+4444444+32',
    '..234444444432',
    '..233333333332',
    '...2222222222',
    '', '', '',
  ],
};

const turretPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '..........2222',
    '.......2222222222',
    '.....222333++333222',
    '....223333.44.333322',
    '...22+3....44....3+22',
    '..22++4....44....4++22',
    '..233444.++++++.444332',
    '.223..44+..44..+44..322',
    '.233...+4..44..4+...332',
    '.233..+..444444..+..332',
    '223...+..455554..+...322',
    '22+444+4445445444+444+22',
    '22+444+4445445444+444+22',
    '223...+..455554..+...322',
    '.233..+..444444..+..332',
    '.233...+4..44..4+...332',
    '.223..44+..44..+44..322',
    '..233444.++++++.444332',
    '..22++4....44....4++22',
    '...22+3....44....3+22',
    '....223333.44.333322',
    '.....222333++333222',
    '.......2222222222',
    '..........2222',
  ],
};

const turretPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '..............2222',
    '..........222222222222',
    '........222333++++333222',
    '.......2233333++++3333322',
    '.....22333334.4444.43333322',
    '....22++334...4444...433++22',
    '....2+4+4......44......4+4+2',
    '...23++444.....++.....444++32',
    '..22334444..++++++++..44443322',
    '..2333.444++...44...++444.3332',
    '.22334...++4...44...4++...43322',
    '.2333....+444..44..444+....3332',
    '.2334...+..4444444444..+...4332',
    '.233....+...44555544...+....332',
    '22++44..+...45444454...+..44++22',
    '22++444++44445444454444++444++22',
    '22++444++44445444454444++444++22',
    '22++44..+...45444454...+..44++22',
    '.233....+...44555544...+....332',
    '.2334...+..4444444444..+...4332',
    '.2333....+444..44..444+....3332',
    '.22334...++4...44...4++...43322',
    '..2333.444++...44...++444.3332',
    '..22334444..++++++++..44443322',
    '...23++444.....++.....444++32',
    '....2+4+4......44......4+4+2',
    '....22++334...4444...433++22',
    '.....22333334.4444.43333322',
    '.......2233333++++3333322',
    '........222333++++333222',
    '..........222222222222',
    '..............2222',
  ],
};

// ───────────────────────────────────────────────────────────────── mortar ──
//
// path 0 cluster      → barrel   one tube, two, four, a five-tube MIRV rack
// path 1 blast        → plate    spades, then outriggers, then seismic anchors
// path 2 incendiary   → crown    a burner ring that grows tongues

const mortarBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 15,
  rows: [
    '', '', '',
    '.............22',
    '.............23',
    '..2222222222234',
    '..2333333333334',
    '..2344++4333334',
    '..2344++4333334',
    '..2333333333334',
    '..2222222222234',
    '.............23',
    '.............22',
    '', '', '',
  ],
};

const mortarBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 16,
  rows: [
    '', '',
    '.............22',
    '..22222222222234',
    '..2344+43333334',
    '..22222222222234',
    '..2333332',
    '..2344++42',
    '..2333332',
    '..22222222222234',
    '..2344+43333334',
    '..22222222222234',
    '.............22',
    '', '', '',
  ],
};

const mortarBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 23,
  rows: [
    '', '', '',
    '...222222',
    '...23333322222222222234',
    '...234+4433333333333334',
    '...23333322222222222234',
    '...2344443',
    '...23333322222222222234',
    '...234+4433333333333334',
    '...23333322222222222234',
    '...2344443',
    '...23333322222222222234',
    '...234+4433333333333334',
    '...23333322222222222234',
    '...2344443',
    '...23333322222222222234',
    '...234+4433333333333334',
    '...23333322222222222234',
    '...222222',
    '', '', '', '',
  ],
};

const mortarBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 30,
  rows: [
    '', '', '', '', '',
    '....22222222',
    '....2333333222222222222222234',
    '....234++443333333333333333334',
    '....2333333222222222222222234',
    '....234444432',
    '....2333333222222222222222234',
    '....234++443333333333333333334',
    '....2333333222222222222222234',
    '....234444432',
    '....2333333222222222222222234',
    '....234++443333333333333333334',
    '....2333333222222222222222234',
    '....234444432',
    '....2333333222222222222222234',
    '....234++443333333333333333334',
    '....2333333222222222222222234',
    '....234444432',
    '....2333333222222222222222234',
    '....234++443333333333333333334',
    '....2333333222222222222222234',
    '....22222222',
    '', '', '', '', '', '',
  ],
};

const mortarCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '.....222222',
    '...23......32',
    '..23........32',
    '..2+........+2',
    '.23..........32',
    '.2+..........+2',
    '.2+..........+2',
    '.23..........32',
    '..2+........+2',
    '..23........32',
    '...23......32',
    '.....222222',
    '', '',
  ],
};

const mortarCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '..........++++',
    '..........3333',
    '.....+.2233333322.+',
    '....+32333222233323+',
    '...+33332......23333+',
    '..+3332..........2333+',
    '...232............232',
    '..233..............332',
    '..232..............232',
    '..33................33',
    '+332................233+',
    '+332................233+',
    '+332................233+',
    '+332................233+',
    '..33................33',
    '..232..............232',
    '..233..............332',
    '...232............232',
    '..+3332..........2333+',
    '...+33332......23333+',
    '....+32333222233323+',
    '.....+.2233333322.+',
    '..........3333',
    '..........++++',
  ],
};

const mortarCrown4: TowerSpriteDef = {
  size: 32,
  spin: 0.18,
  rows: [
    '..............++++',
    '.........+....3333....+',
    '.......+++3.22333322.3+++',
    '......++3333333333333333++',
    '.......333333322223333333',
    '.......233322......223332',
    '...+..2332............2332..+',
    '..++32332..............23323++',
    '..+33332................23333+',
    '.++3332..................2333++',
    '..3333....................3333',
    '...332....................233',
    '..2332....................2332',
    '..233......................332',
    '+3332......................2333+',
    '+3332......................2333+',
    '+3332......................2333+',
    '+3332......................2333+',
    '..233......................332',
    '..2332....................2332',
    '...332....................233',
    '..3333....................3333',
    '.++3332..................2333++',
    '..+33332................23333+',
    '..++32332..............23323++',
    '...+..2332............2332..+',
    '.......233322......223332',
    '.......333333322223333333',
    '......++3333333333333333++',
    '.......+++3.22333322.3+++',
    '.........+....3333....+',
    '..............++++',
  ],
};

const mortarPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '...2222222222',
    '..233333333332',
    '.23334444433332',
    '2334444444444332',
    '2+44444444444+32',
    '2344444++4444432',
    '2344444++4444432',
    '2+44444444444+32',
    '2334444444444332',
    '.23334444433332',
    '..233333333332',
    '...2222222222',
    '', '',
  ],
};

const mortarPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '..........++++',
    '........22+44+22',
    '......2233+44+3322',
    '....2233333++3333322',
    '...2233...4++4...3322',
    '...233....4++4....332',
    '..233......++......332',
    '..23.......44.......32',
    '.233.......44.......332',
    '.233......4554......332',
    '+++344...454454...443+++',
    '+44++++4454444544++++44+',
    '+44++++4454444544++++44+',
    '+++344...454454...443+++',
    '.233......4554......332',
    '.233.......44.......332',
    '..23.......44.......32',
    '..233......++......332',
    '...233....4++4....332',
    '...2233...4++4...3322',
    '....2233333++3333322',
    '......2233+44+3322',
    '........22+44+22',
    '..........++++',
  ],
};

const mortarPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '.............222222',
    '..........222222222222',
    '........2222333333332222',
    '......22223333333333332222',
    '.....2223333333333333333222',
    '....222333...444444...333222',
    '...222333....444444....333222',
    '...22333......++++......33322',
    '..22333...+...+44+...+...33322',
    '..2233...+.+..4++4..+.+...3322',
    '.2233...+..++..44..++..+...3322',
    '.2233....++....44....++....3322',
    '.2333.....+..455554..+.....3332',
    '2233344.....45444454.....4433322',
    '2233344++4..54444445..4++4433322',
    '2233344+4+445444444544+4+4433322',
    '2233344+4+445444444544+4+4433322',
    '2233344++4..54444445..4++4433322',
    '2233344.....45444454.....4433322',
    '.2333.....+..455554..+.....3332',
    '.2233....++....44....++....3322',
    '.2233...+..++..44..++..+...3322',
    '..2233...+.+..4++4..+.+...3322',
    '..22333...+...+44+...+...33322',
    '...22333......++++......33322',
    '...222333....444444....333222',
    '....222333...444444...333222',
    '.....2223333333333333333222',
    '......22223333333333332222',
    '........2222333333332222',
    '..........222222222222',
    '.............222222',
  ],
};

// ────────────────────────────────────────────────────────────────── tesla ──
//
// path 0 chain   → crown   prongs radiating from the cap, two then four then
//                          eight around a node ring
// path 1 stun    → plate   coil windings in the base, ending as an EMP pad
// path 2 voltage → barrel  the discharge horn and the ball it throws

const teslaBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '', '',
    '....2222',
    '...23332',
    '...2+32',
    '...2332....22',
    '...233333.2++2',
    '...233333.2++2',
    '...2332....22',
    '...2+32',
    '...23332',
    '....2222',
    '', '', '',
  ],
};

const teslaBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '', '',
    '...22222',
    '...23332',
    '...2++32',
    '...23332...222',
    '...2333322+++2',
    '...2333322+++2',
    '...23332...222',
    '...2++32',
    '...23332',
    '...22222',
    '', '', '',
  ],
};

const teslaBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 23,
  rows: [
    '', '', '', '', '',
    '.....2222',
    '....233332',
    '....23++32',
    '....2333332',
    '....23++332.....2222',
    '....233333322..233++32',
    '....23++333333323+++++2',
    '....23++333333323+++++2',
    '....233333322..233++32',
    '....23++332.....2222',
    '....2333332',
    '....23++32',
    '....233332',
    '.....2222',
    '', '', '', '', '',
  ],
};

const teslaBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 29,
  rows: [
    '', '', '', '', '',
    '.......22222',
    '......2333332',
    '......23+++32',
    '......2333332',
    '......23+++32',
    '......2333332.........22',
    '......23+++32.......23++2',
    '......233333322....2++44+2',
    '......23+++333322.23+4+44++2',
    '......23+4+33333323+4+5+44+32',
    '......23+4+33333323++5+5+4+32',
    '......23+4+33333323+4+5+44+32',
    '......23+++333322.23+4+44++2',
    '......233333322....2++44+2',
    '......23+++32.......23++2',
    '......2333332.........22',
    '......23+++32',
    '......2333332',
    '......23+++32',
    '......2333332',
    '.......22222',
    '', '', '', '', '', '',
  ],
};

const teslaCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '',
    '......2222',
    '.....2....2',
    '....2......2',
    '.+333......333+',
    '.+333......333+',
    '....2......2',
    '.....2....2',
    '......2222',
    '', '', '', '',
  ],
};

const teslaCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '',
    '...........++',
    '...........33',
    '...........33',
    '...........33',
    '..........2332',
    '........222..222',
    '.......22......22',
    '......22........22',
    '......2..........2',
    '.....22..........22',
    '.+3333............3333+',
    '.+3333............3333+',
    '.....22..........22',
    '......2..........2',
    '......22........22',
    '.......22......22',
    '........222..222',
    '..........2332',
    '...........33',
    '...........33',
    '...........33',
    '...........++',
    '',
  ],
};

const teslaCrown4: TowerSpriteDef = {
  size: 32,
  spin: 0.22,
  rows: [
    '..............++++',
    '...............++',
    '...............33',
    '...............33',
    '.....++........33........++',
    '....+++........33........+++',
    '....++33.......33.......33++',
    '......333......33......333',
    '.......333...333333...333',
    '........3333322..2233333',
    '.........333........333',
    '.........33..........33',
    '.........3............3',
    '........32............23',
    '+.......32............23.......+',
    '++3333333..............3333333++',
    '++3333333..............3333333++',
    '+.......32............23.......+',
    '........32............23',
    '.........3............3',
    '.........33..........33',
    '.........333........333',
    '........3333322..2233333',
    '.......333...333333...333',
    '......333......33......333',
    '....++33.......33.......33++',
    '....+++........33........+++',
    '.....++........33........++',
    '...............33',
    '...............33',
    '...............++',
    '..............++++',
  ],
};

const teslaPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '...22222222',
    '..2333333332',
    '..234444444+2',
    '.2344444444332',
    '.234+4444+4432',
    '.234+4444+4432',
    '.2344444444332',
    '..234444444+2',
    '..2333333332',
    '...22222222',
    '', '', '',
  ],
};

const teslaPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '', '', '',
    '......22222222222',
    '....233333333333332',
    '...23444444444444432',
    '..2344+++++++++++4432',
    '..234+2222222222+4432',
    '.23444222222222224432',
    '.2344+22222222222+432',
    '.234442222222222224432',
    '.234442222222222224432',
    '.2344+22222222222+432',
    '.23444222222222224432',
    '..234+2222222222+4432',
    '..2344+++++++++++4432',
    '...23444444444444432',
    '....233333333333332',
    '......22222222222',
    '', '', '', '',
  ],
};

const teslaPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '', '', '', '',
    '........2222222222222222',
    '......23333333333333333332',
    '.....2344444444444444444432',
    '....234+++++++++++++++++4432',
    '...2344+2222222222222222+4432',
    '...234+22233333333333222+4432',
    '..2344+2233+++++++++3322+44432',
    '..2344223+2222222222+332244332',
    '..234+23+22222222222++32+4432',
    '..234423+22222222222++3224432',
    '..234423+22222222222++3224432',
    '..234+23+22222222222++32+4432',
    '..2344223+2222222222+332244332',
    '..2344+2233+++++++++3322+44432',
    '...234+22233333333333222+4432',
    '...2344+2222222222222222+4432',
    '....234+++++++++++++++++4432',
    '.....2344444444444444444432',
    '......23333333333333333332',
    '........2222222222222222',
    '', '', '', '', '', '',
  ],
};

// ─────────────────────────────────────────────────────────────────── cryo ──
//
// path 0 slow    → barrel  a nozzle that opens into a flare, then a jet bank
// path 1 shatter → crown   fracture shards standing off the cap
// path 2 aura    → plate   a frost ring that grows into a snowflake pad

const cryoBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  rows: [
    '', '', '',
    '..........22',
    '.........233',
    '..22222223334',
    '..23333333334',
    '..2344++33334',
    '..2344++33334',
    '..23333333334',
    '..22222223334',
    '.........233',
    '..........22',
    '', '', '',
  ],
};

const cryoBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  rows: [
    '', '',
    '..........223',
    '.........2334',
    '........23334',
    '..2222223+334',
    '..23333333+34',
    '..2344++33+34',
    '..2344++33+34',
    '..23333333+34',
    '..2222223+334',
    '........23334',
    '.........2334',
    '..........223',
    '', '',
  ],
};

const cryoBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 18,
  rows: [
    '', '', '',
    '..............2223',
    '.............23334',
    '............233+34',
    '...........2333+34',
    '..2222222223333+34',
    '..23333333333333+34',
    '..2344++4333333+34',
    '..2344++4333333+34',
    '..2344++4333333+34',
    '..2344++4333333+34',
    '..2344++4333333+34',
    '..2344++4333333+34',
    '..23333333333333+34',
    '..2222222223333+34',
    '...........2333+34',
    '............233+34',
    '.............23334',
    '..............2223',
    '', '', '',
  ],
};

const cryoBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 22,
  rows: [
    '', '',
    '...................2223',
    '..................23334',
    '.................233+34',
    '................2333+34',
    '...............23333+34',
    '..............233333+34',
    '..222222222222333333+34',
    '..23333333333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..2344++44333333333+34',
    '..23333333333333333+34',
    '..222222222222333333+34',
    '..............233333+34',
    '...............23333+34',
    '................2333+34',
    '.................233+34',
    '..................23334',
    '...................2223',
    '', '',
  ],
};

const cryoCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '.......++',
    '.......33',
    '.......33',
    '.......33',
    '......2..2',
    '.....2....2',
    '.+333......333+',
    '.+333......333+',
    '.....2....2',
    '......2..2',
    '.......33',
    '.......33',
    '.......33',
    '.......++',
    '',
  ],
};

const cryoCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '',
    '......++........++',
    '......++........++',
    '......+33......33+',
    '.......33......33',
    '........33....33',
    '.........322223',
    '........22....22',
    '........2......2',
    '.......2........2',
    '.++33332........23333++',
    '.++33332........23333++',
    '.......2........2',
    '........2......2',
    '........22....22',
    '.........322223',
    '........33....33',
    '.......33......33',
    '......+33......33+',
    '......++........++',
    '......++........++',
    '', '',
  ],
};

const cryoCrown4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '',
    '........++............++',
    '.......+++............+++',
    '........+++..........+++',
    '.........++..........++',
    '.........333........333',
    '..........33........33',
    '...........3........3',
    '...........33......33',
    '............32222223',
    '............22....22',
    '...........2........2',
    '..........22........22',
    '..........2..........2',
    '++++3333332..........2333333++++',
    '++++3333332..........2333333++++',
    '..........2..........2',
    '..........22........22',
    '...........2........2',
    '............22....22',
    '............32222223',
    '...........33......33',
    '...........3........3',
    '..........33........33',
    '.........333........333',
    '.........++..........++',
    '........+++..........+++',
    '.......+++............+++',
    '........++............++',
    '', '',
  ],
};

const cryoPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '....22222222',
    '..223333333322',
    '..233444444332',
    '.23344444444332',
    '.23444++++44432',
    '.2+444++++444+2',
    '.2+444++++444+2',
    '.23444++++44432',
    '.23344444444332',
    '..233444444332',
    '..223333333322',
    '....22222222',
    '', '',
  ],
};

const cryoPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '', '',
    '.......2222222',
    '.....2333......2',
    '....23344......32',
    '...233..44....43',
    '..233....4....4',
    '..234...++++++4....2',
    '.2+3....++++++....+.2',
    '.234.....++++44.....2',
    '.2344444.4++444.444432',
    '.2344444.4++444.444432',
    '.234.....++++44.....2',
    '.2+3....++++++....+.2',
    '..234...++++++4....2',
    '..233....4....4',
    '...233..44....43',
    '....23344......32',
    '.....2333......2',
    '.......2222222',
    '', '', '',
  ],
};

const cryoPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '',
    '..............++',
    '.........+....++....+',
    '..........+..2332..+',
    '...........2',
    '.....++.................++',
    '......++...23......2...++',
    '.......2...33......33',
    '............3+++++33',
    '............+4....+',
    '.............+444+4',
    '..+++........+444+3........+++',
    '..+++2......+44444+3......2+++',
    '.....2233333+44444+3333322',
    '.....2233333+44444+3333322',
    '..+++2......+44444+3......2+++',
    '..+++........+444+3........+++',
    '.............+444+4',
    '............+4....+',
    '............3+++++33',
    '.......2...33......33',
    '......++...23......2...++',
    '.....++.................++',
    '...........2',
    '..........+..2332..+',
    '.........+....++....+',
    '..............++',
    '', '', '',
  ],
};

// ────────────────────────────────────────────────────────────────── laser ──
//
// path 0 ramp    → crown   a radiator fin stack that goes from warm to white
// path 1 split   → barrel  one emitter, two, three, a fan of five
// path 2 cutting → plate   an armoured power block under the emitter

const laserBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  rows: [
    '', '', '', '',
    '...2222',
    '..233332',
    '..234432222',
    '..2344433+++5',
    '..2344433+++5',
    '..234432222',
    '..233332',
    '...2222',
    '', '', '', '',
  ],
};

const laserBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 12,
  rows: [
    '', '', '',
    '...2222',
    '..233332',
    '..2344322225',
    '..234432+++5',
    '..2344332222',
    '..2344332222',
    '..234432+++5',
    '..2344322225',
    '..233332',
    '...2222',
    '', '', '',
  ],
};

const laserBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 17,
  rows: [
    '', '', '', '', '',
    '.....22222',
    '....2333332',
    '....23443222222',
    '....234432+++++5',
    '....234432222',
    '....23443222222',
    '....2344432+++++5',
    '....234432222',
    '....23443222222',
    '....2344432+++++5',
    '....234432222',
    '....2333332',
    '.....22222',
    '', '', '', '', '', '',
  ],
};

const laserBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 20,
  rows: [
    '', '', '', '', '',
    '......222222',
    '.....23333332',
    '.....234432222222',
    '.....234432+++++++5',
    '.....2344322222',
    '.....234432222222',
    '.....2344332+++++++5',
    '.....2344322222',
    '.....234432222222',
    '.....2344332+++++++5',
    '.....2344322222',
    '.....234432222222',
    '.....2344332+++++++5',
    '.....2344322222',
    '.....234432222222',
    '.....2344332+++++++5',
    '.....2344322222',
    '.....234432222222',
    '.....2344332+++++++5',
    '.....2344322222',
    '.....23333332',
    '......222222',
    '', '', '', '', '',
  ],
};

const laserCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '.......33',
    '....3..33..3',
    '...33..33..33',
    '..333++++++333',
    '....++....++',
    '....+......+',
    '.333+......+333',
    '.333+......+333',
    '....+......+',
    '....++....++',
    '..333++++++333',
    '...33..33..33',
    '....3..33..3',
    '.......33',
    '',
  ],
};

const laserCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '',
    '...........44',
    '.......4..4444..4',
    '......443..33..344',
    '......333..33..333',
    '.......3335555333',
    '...43...5++++++5...34',
    '..4433.5+......+5.3344',
    '...3335+........+5333',
    '.....3+..........+3',
    '..4..5+..........+5..4',
    '.44335+..........+53344',
    '.44335+..........+53344',
    '..4..5+..........+5..4',
    '.....3+..........+3',
    '...3335+........+5333',
    '..4433.5+......+5.3344',
    '...43...5++++++5...34',
    '.......3335555333',
    '......333..33..333',
    '......443..33..344',
    '.......4..4444..4',
    '...........44',
    '',
  ],
};

const laserCrown4: TowerSpriteDef = {
  size: 32,
  rows: [
    '..............4444',
    '..........44..4444..44',
    '.........444...44...444',
    '.........444...33...444',
    '.....44...333..33..333...44',
    '....4444..333..33..333..4444',
    '....4443...33..33..33...3444',
    '.....4333..33++55++33..3334',
    '.......333.+55++++55+.333',
    '..44....3355+......+5533....44',
    '.44433...5++........++5...33444',
    '.4443333+5+..........+5+3333444',
    '....33335+............+53333',
    '.......+5..............5+',
    '44.....++..............++.....44',
    '44433335+..............+53333444',
    '44433335+..............+53333444',
    '44.....++..............++.....44',
    '.......+5..............5+',
    '....33335+............+53333',
    '.4443333+5+..........+5+3333444',
    '.44433...5++........++5...33444',
    '..44....3355+......+5533....44',
    '.......333.+55++++55+.333',
    '.....4333..33++55++33..3334',
    '....4443...33..33..33...3444',
    '....4444..333..33..333..4444',
    '.....44...333..33..333...44',
    '.........444...33...444',
    '.........444...44...444',
    '..........44..4444..44',
    '..............4444',
  ],
};

const laserPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '...2222222222',
    '..233333333332',
    '..2344444444432',
    '..234+4444+432',
    '..234444444432',
    '..234444444432',
    '..234+4444+432',
    '..2344444444432',
    '..233333333332',
    '...2222222222',
    '', '', '',
  ],
};

const laserPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '', '',
    '....2222222222222222',
    '...233333333333333332',
    '...234444444444444432',
    '...2+4444444444444+32',
    '...234444444444444432',
    '...23444+++++++444432',
    '...23444+++++++444432',
    '...23444+++++++444432',
    '...234444444444444432',
    '...23444+++++++444432',
    '...23444+++++++444432',
    '...23444+++++++444432',
    '...234444444444444432',
    '...2+4444444444444+32',
    '...234444444444444432',
    '...233333333333333332',
    '....2222222222222222',
    '', '', '', '',
  ],
};

const laserPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '', '',
    '...2222222222222222222222222',
    '..233333333333333333333333332',
    '..234444444444444444444444432',
    '..2+4444444444444444444444+32',
    '..234444444444444444444444432',
    '..2344++++++++++++++++++444432',
    '..2344++++++++++++++++++444432',
    '..234444444444444444444444432',
    '..2344++++++++++++++++++444432',
    '..2344++++++++++++++++++444432',
    '..234444444444444444444444432',
    '..2344++++++++++++++++++444432',
    '..2344++++++++++++++++++444432',
    '..234444444444444444444444432',
    '..2344++++++++++++++++++444432',
    '..2344++++++++++++++++++444432',
    '..234444444444444444444444432',
    '..2344++++++++++++++++++444432',
    '..2344++++++++++++++++++444432',
    '..234444444444444444444444432',
    '..2+4444444444444444444444+32',
    '..234444444444444444444444432',
    '..233333333333333333333333332',
    '...2222222222222222222222222',
    '', '', '', '',
  ],
};

// ─────────────────────────────────────────────────────────────────── flak ──
//
// path 0 pellets → barrel  two stub barrels, four, then a drum-fed cluster
// path 1 cone    → plate   the traverse ring widens and grows gun shields
// path 2 prox    → crown   a radar dish that ends up a spinning array

const flakBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 12,
  rows: [
    '', '', '',
    '..2222',
    '..233332',
    '..2344322222',
    '..234432+++5',
    '..2344322222',
    '..2344322222',
    '..234432+++5',
    '..2344322222',
    '..233332',
    '..2222',
    '', '', '',
  ],
};

const flakBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 12,
  rows: [
    '', '',
    '..22222',
    '..2333332',
    '..234432222',
    '..2344+3+++5',
    '..234432222',
    '..2344332222',
    '..234432222',
    '..2344+3+++5',
    '..234432222',
    '..2333332',
    '..22222',
    '', '', '',
  ],
};

const flakBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 15,
  rows: [
    '', '', '', '',
    '...222222',
    '..23333332',
    '..234++4322222',
    '..2344443+++++5',
    '..234++4322222',
    '..2344443',
    '..234++4322222',
    '..2344443+++++5',
    '..234++4322222',
    '..2344443',
    '..234++4322222',
    '..2344443+++++5',
    '..234++4322222',
    '..23333332',
    '...222222',
    '', '', '', '', '',
  ],
};

const flakBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 19,
  rows: [
    '', '', '', '',
    '...22222222',
    '..2333333332',
    '..234++44322222222',
    '..2344++443+++++++5',
    '..234++44322222222',
    '..234444443',
    '..234++44322222222',
    '..2344++443+++++++5',
    '..234++44322222222',
    '..234444443',
    '..234++44322222222',
    '..2344++443+++++++5',
    '..234++44322222222',
    '..234444443',
    '..234++44322222222',
    '..2344++443+++++++5',
    '..234++44322222222',
    '..234444443',
    '..234++44322222222',
    '..2344++443+++++++5',
    '..234++44322222222',
    '..2333333332',
    '...22222222',
    '', '', '', '', '',
  ],
};

const flakCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '',
    '..........22',
    '..........222',
    '......444..22',
    '......4+4..22',
    '......444..22',
    '...........22',
    '..........222',
    '..........22',
    '', '', '', '',
  ],
};

const flakCrown3: TowerSpriteDef = {
  size: 24,
  spin: 0.35,
  rows: [
    '', '', '', '',
    '...............2',
    '...............322',
    '..............22322',
    '...............2232',
    '................22+2',
    '.................2+2',
    '.........444.....2+2',
    '.........4+4.....2222',
    '.........444.....2222',
    '.................2+2',
    '.................2+2',
    '................22+2',
    '...............2232',
    '..............22322',
    '...............322',
    '...............2',
    '', '', '', '',
  ],
};

const flakCrown4: TowerSpriteDef = {
  size: 32,
  spin: 0.55,
  rows: [
    '', '', '',
    '....................2',
    '....................322',
    '...................333322',
    '...................3333322',
    '...................33333322',
    '..................222333332',
    '....................223333+2',
    '.....................2233++2',
    '......................22++++2',
    '.......................2++++2',
    '.......................2++++2',
    '............444........22+++22',
    '............4+4.........2+++22',
    '............444.........2+++22',
    '.......................22+++22',
    '.......................2++++2',
    '.......................2++++2',
    '......................22++++2',
    '.....................2233++2',
    '....................223333+2',
    '..................222333332',
    '...................33333322',
    '...................3333322',
    '...................333322',
    '....................322',
    '....................2',
    '', '', '',
  ],
};

const flakPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '.....222222',
    '...2233333322',
    '..233444444332',
    '..2+44444444+2',
    '.2334444444433',
    '.2344++++44432',
    '.2344++++44432',
    '.2334444444433',
    '..2+44444444+2',
    '..233444444332',
    '...2233333322',
    '.....222222',
    '', '',
  ],
};

const flakPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '',
    '.........2222',
    '.......22333322',
    '.....22334......2',
    '...22334444......22',
    '..233444444.......32',
    '..2+4..4444......+.2',
    '.234....444.........2',
    '.234....++++++......32',
    '.234....++++++4..44432',
    '.234.....444444444432',
    '.234....++++++44444432',
    '.234....++++++4..44432',
    '.234.....44444......2',
    '..2+....444......+',
    '..233..4444........2',
    '...22334444.......2',
    '.....223344',
    '.......223',
    '.........2222',
    '', '', '',
  ],
};

const flakPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '',
    '.............22222',
    '..........22333..322',
    '........223344.......2',
    '......22334444.........22',
    '....2233444444..........322',
    '...2334.444444...........3',
    '..2334...444444...........332',
    '..2+44....44444...........4+2',
    '.2334......444444..........4433',
    '.2344.....++++++++++.......432',
    '.2344.....++++++++++....444432',
    '.2344.......44444444.444444432',
    '.234......++++++++++4444444432',
    '.234......++++++++++4444444432',
    '.2344.......44444444.444444432',
    '.2344.....++++++++++....444432',
    '.2344.....++++++++++.......432',
    '.2334......444444..........4433',
    '..2+44....44444...........4+2',
    '..2334...444444...........332',
    '...2334.444444...........3',
    '....2233444444..........322',
    '......22334444.........22',
    '........223344.......2',
    '..........22333..322',
    '.............22222',
    '', '', '',
  ],
};

// ─────────────────────────────────────────────────────────────────── acid ──
//
// path 0 stacks    → crown   a battery of reagent tanks, one to six
// path 1 spread    → plate   a spill pool that keeps creeping outward
// path 2 shieldmelt→ barrel  a spray head that sharpens into an injector lance

const acidBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 12,
  rows: [
    '', '', '',
    '..2222',
    '..233332',
    '..2344322',
    '..234432332',
    '..2344323++5',
    '..234432332',
    '..2344322',
    '..233332',
    '..2222',
    '', '', '', '',
  ],
};

const acidBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '',
    '..22222',
    '..2333332',
    '..234+432',
    '..23444322',
    '..234+4323332',
    '..234443233++5',
    '..234+4323332',
    '..23444322',
    '..234+432',
    '..2333332',
    '..22222',
    '', '', '',
  ],
};

const acidBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 17,
  rows: [
    '', '', '', '',
    '...222222',
    '..23333332',
    '..234++432',
    '..2344444322',
    '..234++43222',
    '..23444432332',
    '..234++4323333',
    '..2344444233++++5',
    '..234++4323333',
    '..23444432332',
    '..234++43222',
    '..2344444322',
    '..234++432',
    '..23333332',
    '...222222',
    '', '', '', '', '',
  ],
};

const acidBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 22,
  rows: [
    '', '', '', '', '', '',
    '....2222222',
    '...233333332',
    '...234+++432',
    '...23444444322',
    '...234+++432222',
    '...2344444432332',
    '...234+++43233332',
    '...234444443333333',
    '...234+++4323333333',
    '...23444444233+++++++5',
    '...234+++4323333333',
    '...234444443333333',
    '...234+++43233332',
    '...2344444432332',
    '...234+++432222',
    '...23444444322',
    '...234+++432',
    '...233333332',
    '....2222222',
    '', '', '', '', '', '', '',
  ],
};

const acidCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '.....22222',
    '.....2+++2',
    '.....2+++2',
    '.....2+++2',
    '.....22222',
    '', '', '', '', '',
    '.....22222',
    '.....2+++2',
    '.....2+++2',
    '.....2+++2',
    '.....22222',
    '',
  ],
};

const acidCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '',
    '........222222',
    '........2++++2',
    '........2++++2',
    '........2++++2',
    '........2++++2',
    '........222222',
    '',
    '................222222',
    '.222222.........2++++2',
    '.2++++2.........2++++2',
    '.2++++2.........2++++2',
    '.2++++2.........2++++2',
    '.2++++2.........222222',
    '.222222',
    '',
    '........222222',
    '........2++++2',
    '........2++++2',
    '........2++++2',
    '........2++++2',
    '........222222',
    '', '',
  ],
};

const acidCrown4: TowerSpriteDef = {
  size: 32,
  rows: [
    '',
    '............2222222',
    '............2+++++2',
    '............2+444+2',
    '............2+444+2',
    '............2+444+2',
    '..2222222...2+++++2...2222222',
    '..2+++++2...2222222...2+++++2',
    '..2+444+2.............2+444+2',
    '..2+444+2.............2+444+2',
    '..2+444+2.............2+444+2',
    '..2+++++2.............2+++++2',
    '..2222222.............2222222',
    '', '', '', '', '',
    '..2222222.............2222222',
    '..2+++++2.............2+++++2',
    '..2+444+2.............2+444+2',
    '..2+444+2.............2+444+2',
    '..2+444+2.............2+444+2',
    '..2+++++2...2222222...2+++++2',
    '..2222222...2+++++2...2222222',
    '............2+444+2',
    '............2+444+2',
    '............2+444+2',
    '............2+++++2',
    '............2222222',
    '', '',
  ],
};

const acidPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '....222222',
    '..2233333322',
    '..233444443332',
    '.2334444444332',
    '.23444444444332',
    '.234+444444+32',
    '.234+444444+32',
    '.23444444444332',
    '.2334444444332',
    '..233444443332',
    '..2233333322',
    '....222222',
    '', '',
  ],
};

const acidPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '..........2222',
    '.......2222332222',
    '.....22333333333322',
    '....2233333333333322',
    '...22333443+344433322',
    '..22334444333444443322',
    '..23333444444444333332',
    '.2333+34444444443+33332',
    '.2333334444444443334332',
    '.2334444444554444444332',
    '223344444454454444443322',
    '233444444544445444444332',
    '233444444544445444444332',
    '223344444454454433343322',
    '.2333334444554443+34332',
    '.2333+34444444443334332',
    '.2333334444444444443332',
    '..23344444333444444332',
    '..223344443+3444443322',
    '...223334433344433322',
    '....2233333443333322',
    '.....22333333333322',
    '.......2222332222',
    '..........2222',
  ],
};

const acidPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '.............222222',
    '..........222233332222',
    '........2233333333333322',
    '......22333333333333333322',
    '.....2233334443334444333322',
    '....22333444443+344444433322',
    '...22333444444333444444433322',
    '...23333334444444444433343332',
    '..233343+3444444444443+3443332',
    '..2334433344444444444333444332',
    '.233344444444444444444444443332',
    '.233444444444444444444444444332',
    '.233444444444444444444444444332',
    '22334444444444555544444444443322',
    '23333334444445444454444433343332',
    '23333+3444444544445444443+343332',
    '23333334444445444454444433343332',
    '23334444444445444454444444443332',
    '22334444444444555544444444443322',
    '.233444444444444444444444444332',
    '.233444444444444444444444444332',
    '.233344333444444444443334443332',
    '..233443+3444444444443+3444332',
    '..2333433344444444444333443332',
    '...23334444444333444444443332',
    '...223334444443+3444444433322',
    '....223334444433344444433322',
    '.....2233334444444444333322',
    '......22333333333333333322',
    '........2233333333333322',
    '..........222233332222',
    '.............222222',
  ],
};

// ───────────────────────────────────────────────────────────────── sniper ──
//
// path 0 crit  → crown   the glass. Follows the aim angle, like the Turret's.
// path 1 range → plate   a mast, then a tripod, then an uplink pad
// path 2 AP    → barrel  longer, thicker, finally a rail

const sniperBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  rows: [
    '', '', '', '',
    '..2222',
    '..23332',
    '..2343222222',
    '..234433333+5',
    '..234433333+5',
    '..2343222222',
    '..23332',
    '..2222',
    '', '', '', '',
  ],
};

const sniperBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  rows: [
    '', '', '',
    '..22222',
    '..233332',
    '..2343332',
    '..23443222222',
    '..234443333+5',
    '..234443333+5',
    '..23443222222',
    '..2343332',
    '..233332',
    '..22222',
    '', '', '',
  ],
};

const sniperBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 18,
  rows: [
    '', '', '', '', '', '',
    '...222222',
    '..23333332',
    '..234+++32',
    '..2344443222222222',
    '..2344443333333++5',
    '..234444333333335',
    '..234444333333335',
    '..2344443333333++5',
    '..2344443222222222',
    '..234+++32',
    '..23333332',
    '...222222',
    '', '', '', '', '', '',
  ],
};

const sniperBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 24,
  rows: [
    '', '', '', '', '', '', '', '', '',
    '...2222222',
    '..233333332',
    '..234+++432',
    '..234+++432',
    '..2344444322222222222222',
    '..23444443+++++++++++++5',
    '..2344444333333333333335',
    '..2344444333333333333335',
    '..23444443+++++++++++++5',
    '..2344444322222222222222',
    '..234+++432',
    '..234+++432',
    '..233333332',
    '...2222222',
    '', '', '', '', '', '', '', '', '',
  ],
};

const sniperCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '', '',
    '....22222',
    '...2333332',
    '...23+++32',
    '...2333332',
    '....22222',
    '', '', '', '', '', '',
  ],
};

const sniperCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '', '', '', '', '', '',
    '.......22222',
    '......2333332',
    '......23+++32',
    '.....233+++33222',
    '.....23+555+33335',
    '.....23+555+33335',
    '.....233+++33222',
    '......23+++32',
    '......2333332',
    '.......22222',
    '', '', '', '', '', '', '',
  ],
};

const sniperCrown4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '', '', '', '', '', '',
    '........22222',
    '.......2333332',
    '.......23+++32',
    '......233+++332',
    '......23+++++32',
    '.....2334+++4332222',
    '.....23++55555++33333335',
    '.....23++54445++33333335',
    '.....23++55555++33333335',
    '.....2334+++4332222',
    '......23+++++32',
    '......233+++332',
    '.......23+++32',
    '.......2333332',
    '........22222',
    '', '', '', '', '', '', '', '', '',
  ],
};

const sniperPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '',
    '.....222222',
    '...22333333322',
    '..2334444443332',
    '..2+4444444+2',
    '..234444444432',
    '..2344++++4432',
    '..2344++++4432',
    '..234444444432',
    '..2+4444444+2',
    '..2334444443332',
    '...22333333322',
    '.....222222',
    '', '',
  ],
};

const sniperPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '', '',
    '..2.......22.....2',
    '..232.....332',
    '...2.......43',
    '...........44',
    '...........44',
    '..........4444',
    '..2+.....444444.+',
    '..234...44444444',
    '..23444++++++44432',
    '..23444++++++44432',
    '..234...44444444',
    '..2+.....444444.+',
    '..........4444',
    '...........44',
    '...........44',
    '...2.......43',
    '..232.....332',
    '..2.......22.....2',
    '', '', '',
  ],
};

const sniperPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '', '', '', '',
    '..+..........................+',
    '..232.........2222.........232',
    '...3..........3333..........3',
    '..............4444',
    '..............4444',
    '...............44',
    '...............44',
    '....+........444444.....+',
    '............44444444',
    '...2......++++++++444',
    '...234444.++++++++444..44432',
    '...2344444++5555++4444444432',
    '...2344444++5555++4444444432',
    '...234444.++++++++444..44432',
    '...2......++++++++444',
    '............44444444',
    '....+........444444.....+',
    '...............44',
    '...............44',
    '..............4444',
    '..............4444',
    '...3..........3333..........3',
    '..232.........2222.........232',
    '..+..........................+',
    '', '', '', '',
  ],
};

// ────────────────────────────────────────────────────────────────── pylon ──
//
// path 0 damage → crown   the crystal itself, taller and hotter each bracket
// path 1 rate   → barrel  gyro rings that spin faster with the tier
// path 2 cash   → plate   a vault pad that grows bullion stacks

const pylonBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 10,
  spin: 0.06,
  rows: [
    '', '', '', '', '',
    '.....2222',
    '....233332',
    '....23..32',
    '....23..32',
    '....233332',
    '.....2222',
    '', '', '', '', '',
  ],
};

const pylonBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  spin: 0.18,
  rows: [
    '', '', '',
    '.....2222',
    '...223333222',
    '..23......32',
    '..2+......+2',
    '.23........32',
    '.23........32',
    '..2+......+2',
    '..23......32',
    '...223333222',
    '.....2222',
    '', '', '',
  ],
};

const pylonBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 21,
  spin: 0.4,
  rows: [
    '', '', '',
    '........2222',
    '......22333322',
    '.....23......32',
    '....23........32',
    '...2+..........+2',
    '...23............32',
    '..23..............32',
    '..2+..............+2',
    '.23................32',
    '.23................32',
    '..2+..............+2',
    '..23..............32',
    '...23............32',
    '...2+..........+2',
    '....23........32',
    '.....23......32',
    '......22333322',
    '........2222',
    '', '', '',
  ],
};

const pylonBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 32,
  spin: 0.75,
  rows: [
    '..............3++3',
    '..........33333++33333',
    '........333..........333',
    '.......33..............33',
    '.....+3..................3+',
    '....++......33333333......++',
    '....3.....++........++.....3',
    '...3.....3............3.....3',
    '..33....3..............3....33',
    '..3....3.....33++33.....3....3',
    '.33...3....33......33....3...33',
    '.3....3...33........33...3....3',
    '.3...3....3..........3....3...3',
    '.3...3...3............3...3...3',
    '33...3...3............3...3...33',
    '++...+...+............+...+...++',
    '++...+...+............+...+...++',
    '33...3...3............3...3...33',
    '.3...3...3............3...3...3',
    '.3...3....3..........3....3...3',
    '.3....3...33........33...3....3',
    '.33...3....33......33....3...33',
    '..3....3.....33++33.....3....3',
    '..33....3..............3....33',
    '...3.....3............3.....3',
    '....3.....++........++.....3',
    '....++......33333333......++',
    '.....+3..................3+',
    '.......33..............33',
    '........333..........333',
    '..........33333++33333',
    '..............3++3',
  ],
};

const pylonCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '.......2',
    '......232',
    '.....23+32',
    '.....23+32',
    '....234+432',
    '....234+432',
    '.....23+32',
    '.....23+32',
    '......232',
    '.......2',
    '', '', '',
  ],
};

const pylonCrown3: TowerSpriteDef = {
  size: 24,
  rows: [
    '', '',
    '...........2',
    '..........232',
    '.........23+32',
    '.........23+32',
    '........234+432',
    '........234+432',
    '.......2344+4432',
    '.......234+++432',
    '......2344+++4432',
    '......234+5+5+432',
    '......234+5+5+432',
    '......2344+++4432',
    '.......234+++432',
    '.......2344+4432',
    '........234+432',
    '........234+432',
    '.........23+32',
    '.........23+32',
    '..........232',
    '...........2',
    '', '',
  ],
};

const pylonCrown4: TowerSpriteDef = {
  size: 32,
  rows: [
    '',
    '...............2',
    '..............232',
    '.............23+32',
    '.............23+32',
    '............234+432',
    '............234+432',
    '...........2344+4432',
    '...........234+++432',
    '..........2344+++4432',
    '..........234+5+5+432',
    '.........2344+5+5+4432',
    '.........234+54545+432',
    '........2344+54445+4432',
    '........234+5444445+432',
    '.......2344+5444445+4432',
    '.......2344+5444445+4432',
    '........234+5444445+432',
    '........2344+54445+4432',
    '.........234+54545+432',
    '.........2344+5+5+4432',
    '..........234+5+5+432',
    '..........2344+++4432',
    '...........234+++432',
    '...........2344+4432',
    '............234+432',
    '............234+432',
    '.............23+32',
    '.............23+32',
    '..............232',
    '...............2',
    '',
  ],
};

const pylonPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '...22222222',
    '..2344444432',
    '..23444444432',
    '.23444++44432',
    '.234+4..4+432',
    '.234+4..4+432',
    '.23444++44432',
    '..23444444432',
    '..2344444432',
    '...22222222',
    '', '', '',
  ],
};

const pylonPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '',
    '........33333333',
    '......33+444444+33',
    '....33+++444444+++33',
    '...334+++444444+++433',
    '...344++43333334++443',
    '..3444433......3344443',
    '..34443..........34443',
    '.344443..........344443',
    '.34443............34443',
    '.3++43............34++3',
    '.3++43............34++3',
    '.3++43............34++3',
    '.3++43............34++3',
    '.34443............34443',
    '.344443..........344443',
    '..34443..........34443',
    '..3444433......3344443',
    '...344++43333334++443',
    '...334+++444444+++433',
    '....33+++444444+++33',
    '......33+444444+33',
    '........33333333',
    '',
  ],
};

const pylonPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '..............3333',
    '..........333334433333',
    '........333444++++444333',
    '.......3344444+44+4444433',
    '.....334444444++++444444433',
    '....334+4444444444444444+433',
    '....34+4+444333..333444+4+43',
    '...34+44+433........334+44+43',
    '..3344++43............34++4433',
    '..3444443..............3444443',
    '.3344443................3444433',
    '.3444443................3444443',
    '.344443..................344443',
    '.344443..................344443',
    '33+++43..................34+++33',
    '34+4+4....................4+4+43',
    '34+4+4....................4+4+43',
    '33+++43..................34+++33',
    '.344443..................344443',
    '.344443..................344443',
    '.3444443................3444443',
    '.3344443................3444433',
    '..3444443..............3444443',
    '..3344++43............34++4433',
    '...34+44+433........334+44+43',
    '....34+4+444333..333444+4+43',
    '....334+4444444444444444+433',
    '.....334444444++++444444433',
    '.......3344444+44+4444433',
    '........333444++++444333',
    '..........333334433333',
    '..............3333',
  ],
};

// ──────────────────────────────────────────────────────────── singularity ──
//
// path 0 pull      → barrel  the well ring, spinning harder as it deepens
// path 1 accretion → crown   an infalling disc, brightest at the horizon
// path 2 detonate  → plate   charge cells and warning chevrons

const singularityBarrel0: TowerSpriteDef = {
  size: 16,
  muzzle: 15,
  spin: 0.12,
  rows: [
    '', '', '',
    '.....2222',
    '...223....322',
    '..23........32',
    '..2..........2',
    '.23....++....32',
    '.23....++....32',
    '..2..........2',
    '..23........32',
    '...223....322',
    '.....2222',
    '', '', '',
  ],
};

const singularityBarrel1: TowerSpriteDef = {
  size: 16,
  muzzle: 13,
  spin: 0.3,
  rows: [
    '', '',
    '.....2222',
    '...223333222',
    '..233....332',
    '..23......32',
    '.23...++...32',
    '.2+..++++..+2',
    '.2+..++++..+2',
    '.23...++...32',
    '..23......32',
    '..233....332',
    '...223333222',
    '.....2222',
    '', '',
  ],
};

const singularityBarrel3: TowerSpriteDef = {
  size: 24,
  muzzle: 19,
  spin: 0.6,
  rows: [
    '', '', '',
    '........2222',
    '......22333322',
    '.....23......32',
    '....23........32',
    '...23...2222...32',
    '...2...233332...2',
    '..23..23....32..32',
    '..2+..2..++..2..+2',
    '.23...2.++++.2...32',
    '.23...2.++++.2...32',
    '..2+..2..++..2..+2',
    '..23..23....32..32',
    '...2...233332...2',
    '...23...2222...32',
    '....23........32',
    '.....23......32',
    '......22333322',
    '........2222',
    '', '', '',
  ],
};

const singularityBarrel4: TowerSpriteDef = {
  size: 32,
  muzzle: 32,
  spin: 1.1,
  rows: [
    '', '',
    '............2222',
    '.........223333322',
    '.......223........322',
    '......23..............32',
    '.....2+...222222222...+2',
    '....23..233........332..32',
    '...23..23....2222....32..32',
    '...2+.23...223333322...32+2',
    '..23..2...23........32...2..32',
    '..23.23..23...++++...32..32.32',
    '.2+..2..23...+4444+...32..2..+2',
    '.23..2..2...+4++++4+...2..2..32',
    '.23..2..2...++5555++...2..2..32',
    '+3...2..2...++5555++...2..2...3+',
    '+3...2..2...+4++++4+...2..2...3+',
    '.23..2..2...+444444+...2..2..32',
    '.23..2..2....+4444+....2..2..32',
    '.2+..2..23...+4444+...32..2..+2',
    '..23.23..23...++++...32..32.32',
    '..23..2...23........32...2..32',
    '...2+.23...223333322...32+2',
    '...23..23....2222....32..32',
    '....23..233........332..32',
    '.....2+...222222222...+2',
    '......23..............32',
    '.......223........322',
    '.........223333322',
    '............2222',
    '', '',
  ],
};

const singularityCrown1: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '.......33',
    '....33333333',
    '...333++++333',
    '..333++++++333',
    '..33++5555++33',
    '..3++5....5++3',
    '.33++5....5++33',
    '.33++5....5++33',
    '..3++5....5++3',
    '..33++5555++33',
    '..333++++++333',
    '...333++++333',
    '....33333333',
    '.......33',
    '',
  ],
};

const singularityCrown3: TowerSpriteDef = {
  size: 24,
  spin: 0.25,
  rows: [
    '',
    '...........22',
    '.......2222222222',
    '......222333333222',
    '.....22333333333322',
    '....22333++++++33322',
    '...22333+444444+33322',
    '..22333+44++++44+33322',
    '..2233+44+5555+44+3322',
    '..233+44+5....5+44+332',
    '..233+4+5......5+4+332',
    '.2233+4+5......5+4+3322',
    '.2233+4+5......5+4+3322',
    '..233+4+5......5+4+332',
    '..233+44+5....5+44+332',
    '..2233+44+5555+44+3322',
    '..22333+44++++44+33322',
    '...22333+444444+33322',
    '....22333++++++33322',
    '.....22333333333322',
    '......222333333222',
    '.......2222222222',
    '...........22',
    '',
  ],
};

const singularityCrown4: TowerSpriteDef = {
  size: 32,
  spin: 0.45,
  rows: [
    '..............2222',
    '..........222222222222',
    '........2222223333222222',
    '.......222233333333332222',
    '.....2222333333333333332222',
    '....22233333++++++++33333222',
    '....223333++44444444++333322',
    '...22333++444444444444++33322',
    '..22233+4444444444444444+33222',
    '..22333+4444444444444444+33322',
    '.22233+444444++++++444444+33222',
    '.22333+44444+55..55+44444+33322',
    '.2233+44444+5......5+44444+3322',
    '.2233+4444+5........5+4444+3322',
    '22333+4444+5........5+4444+33322',
    '22333+4444+..........+4444+33322',
    '22333+4444+..........+4444+33322',
    '22333+4444+5........5+4444+33322',
    '.2233+4444+5........5+4444+3322',
    '.2233+44444+5......5+44444+3322',
    '.22333+44444+55..55+44444+33322',
    '.22233+444444++++++444444+33222',
    '..22333+4444444444444444+33322',
    '..22233+4444444444444444+33222',
    '...22333++444444444444++33322',
    '....223333++44444444++333322',
    '....22233333++++++++33333222',
    '.....2222333333333333332222',
    '.......222233333333332222',
    '........2222223333222222',
    '..........222222222222',
    '..............2222',
  ],
};

const singularityPlate1: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '...22222222',
    '..2344444432',
    '.23444444432',
    '.2+4444444+2',
    '.234..2..4432',
    '.234..2..4432',
    '.2+4444444+2',
    '.23444444432',
    '..2344444432',
    '...22222222',
    '', '', '',
  ],
};

const singularityPlate3: TowerSpriteDef = {
  size: 24,
  rows: [
    '',
    '........33333333',
    '......33+444444+33',
    '....33+++444444+++33',
    '...33++++443344++++33',
    '...344+433....334+443',
    '..344443........344443',
    '..34443..........34443',
    '.34443............34443',
    '.34443............34443',
    '.3++4..............4++3',
    '.3++3..............3++3',
    '.3++3..............3++3',
    '.3++4..............4++3',
    '.34443............34443',
    '.34443............34443',
    '..34443..........34443',
    '..344443........344443',
    '...344+433....334+443',
    '...33++++443344++++33',
    '....33+++444444+++33',
    '......33+444444+33',
    '........33333333',
    '',
  ],
};

const singularityPlate4: TowerSpriteDef = {
  size: 32,
  rows: [
    '..............3333',
    '..........333334433333',
    '........3334444444444333',
    '.......33+++44444444+++33',
    '.....33++44+44444444+44++33',
    '....334+4++4333333334++4+433',
    '....3444+433........334+4443',
    '...3444443............3444443',
    '..3344443..............3444433',
    '..344443................344443',
    '.334443..................344433',
    '.344443..................344443',
    '.34443....................34443',
    '.3+443....................344+3',
    '33++43....................34++33',
    '34++43....................34++43',
    '34++43....................34++43',
    '33++43....................34++33',
    '.3+443....................344+3',
    '.34443....................34443',
    '.344443..................344443',
    '.334443..................344433',
    '..344443................344443',
    '..3344443..............3444433',
    '...3444443............3444443',
    '....3444+433........334+4443',
    '....334+4++4333333334++4+433',
    '.....33++44+44444444+44++33',
    '.......33+++44444444+++33',
    '........3334444444444333',
    '..........333334433333',
    '..............3333',
  ],
};


// ───────────────────────────────────────────────────── tier-2 states (16) ──
//
// The fifth rung. Four drawings put tiers 1 and 2 on the same picture, which
// made a 2-1-0 and a 1-2-0 the same tower wearing different brightness — the
// exact confusion the whole layer scheme exists to remove. These are the tier-2
// step: still on the 16 grid, because tier 2 is a modest purchase and the
// crossover rule keeps it small, but a different drawing every time.

const turretBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '', '',
    '..222',
    '..2+2',
    '..2+22222',
    '..23444432222',
    '..234444433335',
    '..2344443',
    '..234444433335',
    '..23444432222',
    '..2222222',
    '', '', '', '',
  ],
};

const mortarBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 16,
  rows: [
    '',
    '.............22',
    '..22222222222234',
    '..2344433333334',
    '..22222222222234',
    '..2333332',
    '..22222222222234',
    '..2344+33333334',
    '..22222222222234',
    '..2333332',
    '..22222222222234',
    '..2344433333334',
    '..22222222222234',
    '.............22',
    '', '',
  ],
};

const teslaBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 16,
  rows: [
    '', '', '',
    '...222222',
    '...2344432',
    '...23++432',
    '...234443222+++2',
    '...23++4322++++2',
    '...23++4322++++2',
    '...234443222+++2',
    '...23++432',
    '...2344432',
    '...222222',
    '', '', '',
  ],
};

const cryoBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 14,
  rows: [
    '', '',
    '.........223',
    '........2334',
    '.......23334',
    '..2222223+334',
    '..233333333+34',
    '..2344++333+34',
    '..2344++333+34',
    '..233333333+34',
    '..2222223+334',
    '.......23334',
    '........2334',
    '.........223',
    '', '',
  ],
};

const laserBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 12,
  rows: [
    '', '',
    '...22222',
    '..2333332',
    '..234432+++5',
    '..2344322222',
    '..2344432',
    '..234432+++5',
    '..2344322222',
    '..2344432',
    '..234432+++5',
    '..2333332',
    '...22222',
    '', '', '',
  ],
};

const flakBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 12,
  rows: [
    '', '',
    '...22222',
    '..2333332',
    '..234432+++5',
    '..2344322222',
    '..2344432',
    '..234432+++5',
    '..234432+++5',
    '..2344432',
    '..2344322222',
    '..234432+++5',
    '..2333332',
    '...22222',
    '', '',
  ],
};

const acidBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 15,
  rows: [
    '', '',
    '..22222',
    '..2333332',
    '..234+432',
    '..23444432',
    '..234+43233332',
    '..2344443233++5',
    '..234+43233332',
    '..23444432',
    '..234+432',
    '..2333332',
    '..22222',
    '', '', '',
  ],
};

const sniperBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 16,
  rows: [
    '', '', '',
    '..22222',
    '..233332',
    '..2344332',
    '..23444322222222',
    '..234443333333+5',
    '..234443333333+5',
    '..23444322222222',
    '..2344332',
    '..233332',
    '..22222',
    '', '', '',
  ],
};

const turretCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '',
    '....222222',
    '...23444432',
    '...234+++432',
    '...234+++43222',
    '...234+++432',
    '...23444432',
    '....222222',
    '', '', '', '', '',
  ],
};

const sniperCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '', '',
    '.....2222',
    '....233332',
    '...23+++32',
    '...23+++322222',
    '...23+++322222',
    '...23+++32',
    '....233332',
    '.....2222',
    '', '', '', '',
  ],
};

const pylonCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '.......2',
    '......232',
    '.....23+32',
    '.....23+32',
    '....234+432',
    '....234+432',
    '....23+5+32',
    '....23+5+32',
    '....234+432',
    '....234+432',
    '.....23+32',
    '.....23+32',
    '......232',
    '.......2',
    '',
  ],
};

const mortarCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '....++2332++',
    '...+33222233+',
    '...32......23',
    '..32........23',
    '.23..........32',
    '+22..........22+',
    '+32..........23+',
    '+32..........23+',
    '+22..........22+',
    '.23..........32',
    '..32........23',
    '...32......23',
    '...+33222233+',
    '....++2332++',
    '',
  ],
};

const teslaCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '....++',
    '...++3',
    '....33',
    '.....33222',
    '.....2....2',
    '....2......2',
    '....2......333++',
    '....2......333++',
    '....2......2',
    '.....2....2',
    '.....33222',
    '....33',
    '...++3',
    '....++',
    '',
  ],
};

const cryoCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '.........++',
    '.........33',
    '..+......3',
    '.++3..2223',
    '...332....2',
    '....2......2',
    '....2......233++',
    '....2......233++',
    '....2......2',
    '...332....2',
    '.++3..2223',
    '..+......3',
    '.........33',
    '.........++',
    '',
  ],
};

const laserCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '....333..333',
    '.....33..33',
    '..33..5555..33',
    '.3335++..++5333',
    '...3+......+3',
    '...5+......+5',
    '.335........533',
    '.335........533',
    '...5+......+5',
    '...3+......+3',
    '.3335++..++5333',
    '..33..5555..33',
    '.....33..33',
    '....333..333',
    '',
  ],
};

const flakCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '', '', '',
    '..........322',
    '..........222',
    '...........232',
    '......444..222',
    '......4+4...22',
    '......444...22',
    '...........222',
    '...........232',
    '..........222',
    '..........322',
    '', '', '',
  ],
};

const acidCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '.....22222',
    '.....2+++2',
    '.....2+++2',
    '.....2+++2',
    '.....22222',
    '', '', '',
    '.22222...22222',
    '.2+++2...2+++2',
    '.2+++2...2+++2',
    '.2+++2...2+++2',
    '.22222...22222',
    '', '',
  ],
};

const singularityCrown2: TowerSpriteDef = {
  size: 16,
  rows: [
    '',
    '....22333322',
    '...2333333332',
    '..233++++++332',
    '.233++++++++332',
    '.23+++5..5+++32',
    '.33++5....5++33',
    '.33++......++33',
    '.33++......++33',
    '.33++5....5++33',
    '.23+++5..5+++32',
    '.233++++++++332',
    '..233++++++332',
    '...2333333332',
    '....22333322',
    '',
  ],
};

const pylonBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 16,
  rows: [
    '.......33',
    '....+33..33+',
    '...3........3',
    '..3..33++33..3',
    '.3..33....33..3',
    '.3.33......33.3',
    '.3.3........3.3',
    '+..+........+..+',
    '+..+........+..+',
    '.3.3........3.3',
    '.3.33......33.3',
    '.3..33....33..3',
    '..3..33++33..3',
    '...3........3',
    '....+33..33+',
    '.......33',
  ],
};

const singularityBarrel2: TowerSpriteDef = {
  size: 16,
  muzzle: 16,
  rows: [
    '.......33',
    '....+333333+',
    '...3+......+3',
    '..3..........3',
    '.33...2222...33',
    '.3...2....2...3',
    '.3..2..++..2..3',
    '++..2.++++.2..++',
    '++..2.++++.2..++',
    '.3..2..++..2..3',
    '.3...2....2...3',
    '.33...2222...33',
    '..3..........3',
    '...3+......+3',
    '....+333333+',
    '.......33',
  ],
};

const pylonPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '.......33',
    '....33444433',
    '...3++4444++3',
    '..34+433334+43',
    '.3443......3443',
    '.344........443',
    '.443........344',
    '3++3........3++3',
    '3++3........3++3',
    '.443........344',
    '.344........443',
    '.3443......3443',
    '..34+433334+43',
    '...3++4444++3',
    '....33444433',
    '.......33',
  ],
};

const singularityPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '.......33',
    '....3+4444+3',
    '...3++4444++3',
    '..34+3....3+43',
    '.3443......3443',
    '.443........344',
    '.44..........44',
    '3+4..........4+3',
    '3+4..........4+3',
    '.44..........44',
    '.443........344',
    '.3443......3443',
    '..34+3....3+43',
    '...3++4444++3',
    '....3+4444+3',
    '.......33',
  ],
};

const cryoPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2222',
    '....+333333+',
    '...2++....++2',
    '..23.+....+.32',
    '.23..++..++..32',
    '.33...4..4...33',
    '23....5555....32',
    '++++..5445..++++',
    '++++..5445..++++',
    '23....5555....32',
    '.33...4..4...33',
    '.23..++..++..32',
    '..23.+....+.32',
    '...2++....++2',
    '....+333333+',
    '......2222',
  ],
};

const acidPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2222',
    '....23333332',
    '...2333+34332',
    '..234433344432',
    '.23444444444432',
    '.33444444444433',
    '2333444554433332',
    '23+344544543+332',
    '2333445445433332',
    '2344444554444432',
    '.33444444444433',
    '.23444333444432',
    '..23443+344432',
    '...2333334332',
    '....23333332',
    '......2222',
  ],
};

const flakPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2222',
    '....233++332',
    '...+334...33+',
    '..++444.....++',
    '.23.444......32',
    '.33..44......33',
    '23....4554...432',
    '2+....54454444+2',
    '2+....54454444+2',
    '23....4554...432',
    '.33..44......33',
    '.23.444......32',
    '..++444.....++',
    '...+334...33+',
    '....233++332',
    '......2222',
  ],
};

const mortarPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2++2',
    '....223++322',
    '...233.++.332',
    '..23...++...32',
    '.23....++....32',
    '.23....44....32',
    '23....5555....32',
    '+++++454454+++++',
    '+++++454454+++++',
    '23....5555....32',
    '.23....44....32',
    '.23....++....32',
    '..23...++...32',
    '...233.++.332',
    '....223++322',
    '......2++2',
  ],
};

const turretPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2222',
    '....233++332',
    '...233.44.332',
    '..234..44..432',
    '.2344++44++4432',
    '.33.+4....4+.33',
    '23..+.4554.+..32',
    '2+444.5445.444+2',
    '2+444.5445.444+2',
    '23..+.4554.+..32',
    '.33.+4....4+.33',
    '.2344++44++4432',
    '..234..44..432',
    '...233.44.332',
    '....233++332',
    '......2222',
  ],
};

const teslaPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2222',
    '....23333332',
    '...23++44++32',
    '..2++444444++2',
    '.23+44++++44+32',
    '.3+44+4444+44+3',
    '23+4+445544+4+32',
    '2344+454454+4432',
    '2344+454454+4432',
    '23+4+445544+4+32',
    '.3+44+4444+44+3',
    '.23+44++++44+32',
    '..2++444444++2',
    '...23++44++32',
    '....23333332',
    '......2222',
  ],
};

const laserPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2222',
    '....23333332',
    '...2334444332',
    '..234444444432',
    '.+344444444443+',
    '.+++44444444+++',
    '2+++++4554+++++2',
    '2+++++5445+++++2',
    '2+++++5445+++++2',
    '2+++++4554+++++2',
    '.+++44444444+++',
    '.+344444444443+',
    '..234444444432',
    '...2334444332',
    '....23333332',
    '......2222',
  ],
};

const sniperPlate2: TowerSpriteDef = {
  size: 16,
  rows: [
    '......2+2',
    '....22222222',
    '...233.33.332',
    '..23...44...32',
    '.23....44....32',
    '.23....44....32',
    '22....4554....22',
    '+2344454454442+2',
    '2234445445444222',
    '.2....4554....2',
    '.23....44....32',
    '.23....44....32',
    '..23...44...32',
    '...233.22.332',
    '....222+2222',
    '......222',
  ],
};

// ──────────────────────────────────────────────────────────────── registry ──

export const TOWER_SPRITES = {
  plateLight, plateMedium, plateHeavy, plateSupport,

  turretBarrel0, turretBarrel1, turretBarrel3, turretBarrel4, turretCrown1, turretCrown3, turretCrown4, turretPlate1, turretPlate3, turretPlate4, turretBarrel2, turretCrown2, turretPlate2,

  mortarBarrel0, mortarBarrel1, mortarBarrel3, mortarBarrel4, mortarCrown1, mortarCrown3, mortarCrown4, mortarPlate1, mortarPlate3, mortarPlate4, mortarBarrel2, mortarCrown2, mortarPlate2,

  teslaBarrel0, teslaBarrel1, teslaBarrel3, teslaBarrel4, teslaCrown1, teslaCrown3, teslaCrown4, teslaPlate1, teslaPlate3, teslaPlate4, teslaBarrel2, teslaCrown2, teslaPlate2,

  cryoBarrel0, cryoBarrel1, cryoBarrel3, cryoBarrel4, cryoCrown1, cryoCrown3, cryoCrown4, cryoPlate1, cryoPlate3, cryoPlate4, cryoBarrel2, cryoCrown2, cryoPlate2,

  laserBarrel0, laserBarrel1, laserBarrel3, laserBarrel4, laserCrown1, laserCrown3, laserCrown4, laserPlate1, laserPlate3, laserPlate4, laserBarrel2, laserCrown2, laserPlate2,

  flakBarrel0, flakBarrel1, flakBarrel3, flakBarrel4, flakCrown1, flakCrown3, flakCrown4, flakPlate1, flakPlate3, flakPlate4, flakBarrel2, flakCrown2, flakPlate2,

  acidBarrel0, acidBarrel1, acidBarrel3, acidBarrel4, acidCrown1, acidCrown3, acidCrown4, acidPlate1, acidPlate3, acidPlate4, acidBarrel2, acidCrown2, acidPlate2,

  sniperBarrel0, sniperBarrel1, sniperBarrel3, sniperBarrel4, sniperCrown1, sniperCrown3, sniperCrown4, sniperPlate1, sniperPlate3, sniperPlate4, sniperBarrel2, sniperCrown2, sniperPlate2,

  pylonBarrel0, pylonBarrel1, pylonBarrel3, pylonBarrel4, pylonCrown1, pylonCrown3, pylonCrown4, pylonPlate1, pylonPlate3, pylonPlate4, pylonCrown2, pylonBarrel2, pylonPlate2,

  singularityBarrel0, singularityBarrel1, singularityBarrel3, singularityBarrel4, singularityCrown1, singularityCrown3, singularityCrown4, singularityPlate1, singularityPlate3, singularityPlate4, singularityCrown2, singularityBarrel2, singularityPlate2,
} as const satisfies Record<string, TowerSpriteDef>;

export type TowerSpriteName = keyof typeof TOWER_SPRITES;

export const TOWER_SPRITE_NAMES = Object.keys(TOWER_SPRITES) as TowerSpriteName[];

export function validateTowerSprites(): void {
  for (const name of TOWER_SPRITE_NAMES) {
    const s: TowerSpriteDef = TOWER_SPRITES[name];
    if (s.rows.length !== s.size) {
      throw new Error(`tower sprite "${name}": ${s.rows.length} rows, expected ${s.size}`);
    }
    for (let y = 0; y < s.rows.length; y++) {
      const row = s.rows[y];
      if (row.length > s.size) {
        throw new Error(`tower sprite "${name}" row ${y}: ${row.length} chars, max ${s.size}`);
      }
      for (let x = 0; x < row.length; x++) {
        const c = row[x];
        if (c !== '.' && c !== '+' && (c < '1' || c > '5')) {
          throw new Error(`tower sprite "${name}" row ${y} col ${x}: bad char "${c}"`);
        }
      }
    }
  }
}

// ──────────────────────────────────────────────────────────── path bindings ──

/** Which of the three stacked sprites a path drives. */
export type LayerKind = 'plate' | 'barrel' | 'crown';

export interface TowerArt {
  /** Upgrade path index feeding each layer. The three are always a permutation. */
  readonly barrelPath: 0 | 1 | 2;
  readonly crownPath: 0 | 1 | 2;
  readonly platePath: 0 | 1 | 2;
  /** Five states, indexed by {@link BRACKET}. */
  readonly barrel: readonly TowerSpriteName[];
  readonly plate: readonly TowerSpriteName[];
  /** State 0 is `null`: an un-upgraded tower has no crown to draw. */
  readonly crown: readonly (TowerSpriteName | null)[];
  /**
   * Whether the crown turns with the gun. A scope must; a coolant ring must
   * not, or it reads as the whole tower spinning.
   */
  readonly crownFollowsAim: boolean;
}

/**
 * Tier to drawing. Five drawings for six tiers: only 3 and 4 share one, and
 * they share the *largest* silhouette, where a single-tier step would have been
 * lost anyway. Everything below that gets its own picture, so a 2-1-0 and a
 * 1-2-0 are different shapes rather than different brightnesses.
 */
export const BRACKET: readonly number[] = [0, 1, 2, 3, 3, 4];

/**
 * Per-archetype art, keyed by `TowerArchetype.id`.
 *
 * The path-to-layer assignment is the whole design. It is read off what each
 * path does in `game/towers/Catalog.ts`, not imposed:
 *
 * | tower | barrel | crown | plate |
 * |---|---|---|---|
 * | turret      | rate: more guns          | precision: optics      | mass: foundation |
 * | mortar      | cluster: more tubes      | incendiary: burner ring| blast: outriggers |
 * | tesla       | voltage: discharge horn  | chain: prong star      | stun: coil pad |
 * | cryo        | slow: nozzle flare       | shatter: shards        | aura: frost pad |
 * | laser       | split: emitter comb      | ramp: radiator collar  | cutting: power block |
 * | flak        | pellets: barrel bank     | prox: radar dish       | cone: traverse ring |
 * | acid        | shieldmelt: lance        | stacks: tank battery   | spread: spill pool |
 * | sniper      | AP: longer, then a rail  | crit: glass            | range: mast, then uplink |
 * | pylon       | rate: gyro rings         | damage: the crystal    | cash: vault pad |
 * | singularity | pull: the well ring      | accretion: the disc    | detonate: charge pad |
 */
export const TOWER_ART: Readonly<Record<string, TowerArt>> = {
  turret: {
    barrelPath: 0, crownPath: 1, platePath: 2, crownFollowsAim: true,
    barrel: ['turretBarrel0', 'turretBarrel1', 'turretBarrel2', 'turretBarrel3', 'turretBarrel4'],
    crown: [null, 'turretCrown1', 'turretCrown2', 'turretCrown3', 'turretCrown4'],
    plate: ['plateMedium', 'turretPlate1', 'turretPlate2', 'turretPlate3', 'turretPlate4'],
  },
  mortar: {
    barrelPath: 0, crownPath: 2, platePath: 1, crownFollowsAim: false,
    barrel: ['mortarBarrel0', 'mortarBarrel1', 'mortarBarrel2', 'mortarBarrel3', 'mortarBarrel4'],
    crown: [null, 'mortarCrown1', 'mortarCrown2', 'mortarCrown3', 'mortarCrown4'],
    plate: ['plateHeavy', 'mortarPlate1', 'mortarPlate2', 'mortarPlate3', 'mortarPlate4'],
  },
  tesla: {
    barrelPath: 2, crownPath: 0, platePath: 1, crownFollowsAim: false,
    barrel: ['teslaBarrel0', 'teslaBarrel1', 'teslaBarrel2', 'teslaBarrel3', 'teslaBarrel4'],
    crown: [null, 'teslaCrown1', 'teslaCrown2', 'teslaCrown3', 'teslaCrown4'],
    plate: ['plateMedium', 'teslaPlate1', 'teslaPlate2', 'teslaPlate3', 'teslaPlate4'],
  },
  cryo: {
    barrelPath: 0, crownPath: 1, platePath: 2, crownFollowsAim: false,
    barrel: ['cryoBarrel0', 'cryoBarrel1', 'cryoBarrel2', 'cryoBarrel3', 'cryoBarrel4'],
    crown: [null, 'cryoCrown1', 'cryoCrown2', 'cryoCrown3', 'cryoCrown4'],
    plate: ['plateMedium', 'cryoPlate1', 'cryoPlate2', 'cryoPlate3', 'cryoPlate4'],
  },
  laser: {
    barrelPath: 1, crownPath: 0, platePath: 2, crownFollowsAim: false,
    barrel: ['laserBarrel0', 'laserBarrel1', 'laserBarrel2', 'laserBarrel3', 'laserBarrel4'],
    crown: [null, 'laserCrown1', 'laserCrown2', 'laserCrown3', 'laserCrown4'],
    plate: ['plateMedium', 'laserPlate1', 'laserPlate2', 'laserPlate3', 'laserPlate4'],
  },
  flak: {
    barrelPath: 0, crownPath: 2, platePath: 1, crownFollowsAim: true,
    barrel: ['flakBarrel0', 'flakBarrel1', 'flakBarrel2', 'flakBarrel3', 'flakBarrel4'],
    crown: [null, 'flakCrown1', 'flakCrown2', 'flakCrown3', 'flakCrown4'],
    plate: ['plateLight', 'flakPlate1', 'flakPlate2', 'flakPlate3', 'flakPlate4'],
  },
  acid: {
    barrelPath: 2, crownPath: 0, platePath: 1, crownFollowsAim: false,
    barrel: ['acidBarrel0', 'acidBarrel1', 'acidBarrel2', 'acidBarrel3', 'acidBarrel4'],
    crown: [null, 'acidCrown1', 'acidCrown2', 'acidCrown3', 'acidCrown4'],
    plate: ['plateLight', 'acidPlate1', 'acidPlate2', 'acidPlate3', 'acidPlate4'],
  },
  sniper: {
    barrelPath: 2, crownPath: 0, platePath: 1, crownFollowsAim: true,
    barrel: ['sniperBarrel0', 'sniperBarrel1', 'sniperBarrel2', 'sniperBarrel3', 'sniperBarrel4'],
    crown: [null, 'sniperCrown1', 'sniperCrown2', 'sniperCrown3', 'sniperCrown4'],
    plate: ['plateLight', 'sniperPlate1', 'sniperPlate2', 'sniperPlate3', 'sniperPlate4'],
  },
  pylon: {
    barrelPath: 1, crownPath: 0, platePath: 2, crownFollowsAim: false,
    barrel: ['pylonBarrel0', 'pylonBarrel1', 'pylonBarrel2', 'pylonBarrel3', 'pylonBarrel4'],
    crown: [null, 'pylonCrown1', 'pylonCrown2', 'pylonCrown3', 'pylonCrown4'],
    plate: ['plateSupport', 'pylonPlate1', 'pylonPlate2', 'pylonPlate3', 'pylonPlate4'],
  },
  singularity: {
    barrelPath: 0, crownPath: 1, platePath: 2, crownFollowsAim: false,
    barrel: ['singularityBarrel0', 'singularityBarrel1', 'singularityBarrel2', 'singularityBarrel3', 'singularityBarrel4'],
    crown: [null, 'singularityCrown1', 'singularityCrown2', 'singularityCrown3', 'singularityCrown4'],
    plate: ['plateSupport', 'singularityPlate1', 'singularityPlate2', 'singularityPlate3', 'singularityPlate4'],
  },
};

/** Art for an archetype id. Throws rather than drawing the wrong tower. */
export function artOf(id: string): TowerArt {
  const a = TOWER_ART[id];
  if (!a) throw new Error(`towerSprites: no art registered for tower "${id}"`);
  return a;
}

/**
 * Every sprite reachable from `TOWER_ART`, plus the shape checks. Called once
 * by the atlas bake, which turns a typo in a row string into a loud boot
 * failure rather than a silently sheared sprite.
 */
export function validateTowerArt(): void {
  validateTowerSprites();
  if (BRACKET.length !== 6) throw new Error('towerSprites: BRACKET must cover six tiers');
  for (const id of Object.keys(TOWER_ART)) {
    const a = TOWER_ART[id];
    const paths = [a.barrelPath, a.crownPath, a.platePath].sort();
    if (paths[0] !== 0 || paths[1] !== 1 || paths[2] !== 2) {
      throw new Error(`towerSprites: "${id}" does not map each path to its own layer`);
    }
    const check = (kind: string, names: readonly (TowerSpriteName | null)[]) => {
      if (names.length !== 5) throw new Error(`towerSprites: "${id}" ${kind} needs five states`);
      for (const n of names) {
        if (n !== null && !(n in TOWER_SPRITES)) {
          throw new Error(`towerSprites: "${id}" ${kind} references unknown sprite "${n}"`);
        }
      }
    };
    check('barrel', a.barrel);
    check('plate', a.plate);
    check('crown', a.crown);
    if (a.crown[0] !== null) throw new Error(`towerSprites: "${id}" crown state 0 must be null`);
  }
}
